import React from 'react';
import { GeneratedDataItem } from './types';

// Define the necessary props for this component
interface PeopleComponentProps {
  item: GeneratedDataItem;
  editMode: boolean;
  editData: Record<string, any>;
  handleInputChange: (id: string, field: string, value: any, componentType?: string) => void;
}

const PeopleComponent: React.FC<PeopleComponentProps> = ({ item, editMode, editData, handleInputChange }) => {
  if (!item.people) return null;
  // Canonicalize people to a stable local object so TS narrows the type
  const people: Record<string, any> = item.people || {};

  // Helper function to check if a person has data
  const hasPersonData = (personNum: number) => {
    const currentData = editData?.people || people;
    const name = currentData[`name${personNum}`];
    const designation = currentData[`designation${personNum}`];
    const description = currentData[`description${personNum}`];
    return (name && name.trim() !== '') || (designation && designation.trim() !== '') || (description && description.trim() !== '');
  };

  // Get the number of people that have data
  const peopleWithData = [];
  for (let i = 1; i <= 4; i++) {
    if (hasPersonData(i)) {
      peopleWithData.push(i);
    }
  }

  // In edit mode, show all people with data plus one empty slot for adding new person (max 4)
  const peopleToShow = editMode ? 
    [...peopleWithData, ...(peopleWithData.length < 4 ? [peopleWithData.length + 1] : [])].slice(0, 4) : 
    peopleWithData;
  
  if (editMode) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title:</label>
          <input 
            type="text" 
            value={(editData?.people?.title || item.people.title)}
            onChange={(e) => handleInputChange(item._id || '', 'people.title', e.target.value, 'people')}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Overview:</label>
          <textarea 
            value={(editData?.people?.overview || item.people.overview)} 
            onChange={(e) => handleInputChange(item._id || '', 'people.overview', e.target.value, 'people')}
            className="w-full p-2 border border-gray-300 rounded min-h-[100px]"
          />
        </div>
        
        {/* Dynamic People Rendering */}
        {peopleToShow.map((personNum) => (
          <div key={personNum} className="border p-3 rounded">
            <h5 className="font-medium mb-2">Person {personNum}</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name:</label>
                <input 
                  type="text" 
                  value={(editData?.people?.[`name${personNum}`] ?? people[`name${personNum}`] ?? '')}
                  onChange={(e) => handleInputChange(item._id || '', `people.name${personNum}`, e.target.value, 'people')}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Designation:</label>
                <input 
                  type="text" 
                  value={(editData?.people?.[`designation${personNum}`] ?? people[`designation${personNum}`] ?? '')}
                  onChange={(e) => handleInputChange(item._id || '', `people.designation${personNum}`, e.target.value, 'people')}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Description:</label>
                <textarea 
                  value={(editData?.people?.[`description${personNum}`] ?? people[`description${personNum}`] ?? '')}
                  onChange={(e) => handleInputChange(item._id || '', `people.description${personNum}`, e.target.value, 'people')}
                  className="w-full p-2 border border-gray-300 rounded min-h-[60px]"
                />
              </div>
            </div>
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Citation:</label>
          <input 
            type="text" 
            value={(editData?.people?.citation ?? people.citation ?? '')}
            onChange={(e) => handleInputChange(item._id || '', 'people.citation', e.target.value, 'people')}
            className="w-full p-2 border border-gray-300 rounded"
          />
        </div>
      </div>
    );
  }

  // Render people for viewing - only show people with data
  return (
    <>
  <h4 className="text-lg font-bold mb-2">{people.title ?? ''}</h4>
      <p className="mb-3 text-gray-700">{people.overview ?? ''}</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {peopleToShow.map((personNum) => {
          const name = (people[`name${personNum}`] ?? '') as string;
          const designation = (people[`designation${personNum}`] ?? '') as string;
          const description = (people[`description${personNum}`] ?? '') as string;
          
          return (
            <div key={personNum} className="border p-3 rounded shadow-sm">
              <h5 className="font-bold">{name}</h5>
              <p className="text-blue-600">{designation}</p>
              <p className="mt-2 text-sm">{description}</p>
            </div>
          );
        })}
      </div>
      
      {people.citation && (
        <p className="text-sm text-gray-500 mt-4">Source: {people.citation}</p>
      )}
    </>
  );
};

export default PeopleComponent;